import React, { useEffect, useState } from 'react';
import { Container, Card } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { renderMarkdown } from '../components/MarkdownRenderer';
import { ARTICLES_BY_SLUG } from './BlogArticle';

function extractTitleFromMarkdown(text) {
  const match = text.trim().match(/^Title:\s*(.+?)(\n|$)/im);
  return match ? match[1].trim() : null;
}

function stripTitleLineFromMarkdown(text) {
  const trimmed = text.trimStart();
  const titleMatch = trimmed.match(/^Title:\s*(.+?)(\n|$)/im);
  if (titleMatch) {
    return trimmed.slice(trimmed.indexOf(titleMatch[0]) + titleMatch[0].length).trimStart();
  }
  return text;
}

const Article = () => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const article = ARTICLES_BY_SLUG[slug];
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!article) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    setLoading(true);
    setContent('');
    const url = (process.env.PUBLIC_URL || '') + article.markdownFile;
    fetch(url)
      .then((res) => {
        if (!res.ok) {
          setNotFound(true);
          setLoading(false);
          return null;
        }
        return res.text();
      })
      .then((text) => {
        if (text == null) return;
        setContent(text);
        setLoading(false);
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [article]);

  if (!article) {
    return (
      <div className="blog-page-wrapper">
        <Container className="page-container">
          <h1 className="page-title">{t('articles.articleNotFound')}</h1>
          <p className="page-content">{t('articles.articleNotFoundDescription')}</p>
          <Link to="/blog" className="btn btn-primary">
            {t('articles.backToBlog')}
          </Link>
        </Container>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="blog-page-wrapper">
        <Container className="page-container">
          <p className="page-content">{t('common.loading')}</p>
        </Container>
      </div>
    );
  }

  if (notFound || !content) {
    return (
      <div className="blog-page-wrapper">
        <Container className="page-container">
          <h1 className="page-title">{t('articles.articleNotFound')}</h1>
          <p className="page-content">{t('articles.articleNotFoundDescription')}</p>
          <Link to="/blog" className="btn btn-primary">
            {t('articles.backToBlog')}
          </Link>
        </Container>
      </div>
    );
  }

  const title = extractTitleFromMarkdown(content) || t('articles.articleOneTitle');
  const body = stripTitleLineFromMarkdown(content);
  const firstPara = body.trim().split(/\n\n+/)[0]?.replace(/\*\*|#/g, '').trim() || '';
  const description = (firstPara.substring(0, 160) + (firstPara.length > 160 ? '...' : '')) || title;
  const keywords = article.keywords || t('articles.articleOneKeywords');

  return (
    <div className="blog-page-wrapper">
      <Container className="page-container">
        <SEO
          title={`${title} - Osailu`}
          description={description}
          keywords={keywords}
          url={article.path}
        />
        <Link
          to="/blog"
          className="blog-article-link"
          style={{ marginBottom: '1rem', display: 'inline-block' }}
        >
          ← {t('articles.backToBlog')}
        </Link>
        <Card className="mt-3">
          <Card.Body>
            <h1 className="page-title" style={{ marginBottom: '1rem' }}>
              {title}
            </h1>
            <p className="text-muted" style={{ marginBottom: '2rem', fontSize: '0.9rem' }}>
              {new Date(article.date).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
            <div className="page-content">
              {renderMarkdown(body)}
            </div>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default Article;
